/**
 * useFeedbackMode — Shared feedback state + executeNode wrapper
 *
 * Manages feedbackMode toggle, feedbackText state, and a handleRefine
 * callback that writes in_feedback to YDoc and calls executeNode.
 *
 * V2 migration: Uses V2 CanvasContext imports. Self-contained — no
 * imports from old canvas/ directory.
 */

import { useState, useCallback } from 'react'
import { useReactFlow } from 'reactflow'
import { executeNode } from '../../../api/client'
import { useProjectId } from '../../../contexts/ProjectIdContext'
import { useCanvasOptional } from '../CanvasContext'

interface UseFeedbackModeOptions {
  nodeId: string
  status: string | undefined
}

interface UseFeedbackModeReturn {
  feedbackMode: boolean
  feedbackText: string
  setFeedbackText: (text: string) => void
  toggleFeedbackMode: () => void
  handleRefine: () => Promise<void>
}

/**
 * Shared hook for feedback/refinement mode across designer panels.
 *
 * Manages feedbackMode + feedbackText state and provides a handleRefine
 * callback that writes in_feedback to YDoc, then calls executeNode.
 * All node data is already in YDoc — no config parameter needed.
 *
 * Guards:
 * - Returns early if feedbackText is empty or status is 'running'
 * - Sets error state if projectId is missing (no silent failure)
 */
export function useFeedbackMode({
  nodeId,
  status,
}: UseFeedbackModeOptions): UseFeedbackModeReturn {
  const projectId = useProjectId()
  const { setNodes } = useReactFlow()
  const canvas = useCanvasOptional()
  const [feedbackMode, setFeedbackMode] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')

  const toggleFeedbackMode = useCallback(() => {
    setFeedbackMode((prev) => !prev)
  }, [])

  const handleRefine = useCallback(
    async () => {
      if (status === 'running' || !feedbackText.trim()) return

      if (!projectId) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
              : n
          )
        )
        return
      }

      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
            : n
        )
      )

      try {
        // Write feedback to YDoc before executing
        canvas?.updateNodeData(nodeId, { in_feedback: feedbackText.trim() })
        await executeNode(projectId, nodeId)
        setFeedbackText('')
        setFeedbackMode(false)
      } catch (err) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    },
    [projectId, nodeId, status, setNodes, feedbackText, canvas]
  )

  return { feedbackMode, feedbackText, setFeedbackText, toggleFeedbackMode, handleRefine }
}
